/**
 * Display Settings Redux Slice — T146P2
 *
 * Global state for itemized transaction UI preferences:
 * - itemDisplayDensity: compact|receipt|detailed
 * - itemColumnsByPage: per-page column visibility
 *
 * Syncs with backend UserPreferences on mutations.
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export interface ColumnVisibility {
  emoji: boolean;
  qty: boolean;
  category: boolean;
  notes: boolean;
}

export interface DisplaySettingsState {
  itemDisplayDensity: 'compact' | 'receipt' | 'detailed';
  itemColumnsByPage: Record<string, ColumnVisibility>;
  itemColumns: ColumnVisibility;
}

const INITIAL_STATE: DisplaySettingsState = {
  itemDisplayDensity: 'receipt',
  itemColumnsByPage: {
    transactions: { emoji: true, qty: true, category: true, notes: false },
    detail: { emoji: true, qty: true, category: true, notes: false },
    form: { emoji: true, qty: true, category: true, notes: false },
    review: { emoji: true, qty: true, category: true, notes: false },
  },
  itemColumns: { emoji: true, qty: true, category: true, notes: false },
};

export const displaySettingsSlice = createSlice({
  name: 'displaySettings',
  initialState: INITIAL_STATE,
  reducers: {
    setItemDisplayDensity: (state, action: PayloadAction<'compact' | 'receipt' | 'detailed'>) => {
      state.itemDisplayDensity = action.payload;
    },
    setItemColumnVisibility: (state, action: PayloadAction<{ page?: string; visibility: ColumnVisibility }>) => {
      const { page, visibility } = action.payload;
      if (page) {
        state.itemColumnsByPage[page] = visibility;
      }
      state.itemColumns = visibility;
    },
    initializeFromUserPreferences: (state, action: PayloadAction<{
      itemDisplayDensity?: 'compact' | 'receipt' | 'detailed';
      itemColumnsByPage?: Record<string, Record<string, boolean>>;
      itemColumns?: Record<string, boolean>;
    }>) => {
      if (action.payload.itemDisplayDensity) {
        state.itemDisplayDensity = action.payload.itemDisplayDensity;
      }
      if (action.payload.itemColumnsByPage) {
        // Map Record<string, Record<string, boolean>> to Record<string, ColumnVisibility>
        const mapped: Record<string, ColumnVisibility> = {};
        for (const [key, val] of Object.entries(action.payload.itemColumnsByPage)) {
          mapped[key] = {
            emoji: Boolean(val.emoji),
            qty: Boolean(val.qty),
            category: Boolean(val.category),
            notes: Boolean(val.notes),
          };
        }
        state.itemColumnsByPage = mapped;
      }
      if (action.payload.itemColumns) {
        state.itemColumns = {
          emoji: Boolean(action.payload.itemColumns.emoji),
          qty: Boolean(action.payload.itemColumns.qty),
          category: Boolean(action.payload.itemColumns.category),
          notes: Boolean(action.payload.itemColumns.notes),
        };
      }
    },
  },
});

export const {
  setItemDisplayDensity,
  setItemColumnVisibility,
  initializeFromUserPreferences,
} = displaySettingsSlice.actions;

export default displaySettingsSlice.reducer;
